import React from 'react';
import './APIDocsPage.css';

export default function APIDocsPage() {
  return (
    <div className="api-docs-page">
      <section className="api-hero">
        <h1>RESTful API Documentation</h1>
        <p>Use our secure REST API to manage users, calls, recordings, and integrations programmatically.</p>
      </section>
      <section className="api-overview">
        <h2>Overview</h2>
        <p><strong>Base URL:</strong> https://api.voiceproconnect.com/v1</p>
        <p><strong>Authentication:</strong> Bearer token via <code>Authorization: Bearer &lt;token&gt;</code> header.</p>
      </section>
      <section className="api-endpoints">
        <h2>Endpoints</h2>
        <article className="endpoint">
          <h3>GET /v1/users</h3>
          <p>Retrieve a list of users.</p>
          <h4>Response</h4>
          <pre><code>{`
{
  "data": [
    { "id": "user_123", "name": "Alice" },
    { "id": "user_456", "name": "Bob" }
  ]
}
`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>GET /v1/users/:id</h3>
          <p>Retrieve a single user by ID.</p>
          <h4>Parameters</h4>
          <table>
            <thead><tr><th>Name</th><th>In</th><th>Type</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td>id</td><td>path</td><td>string</td><td>User identifier</td></tr>
            </tbody>
          </table>
        </article>
        <article className="endpoint">
          <h3>POST /v1/users</h3>
          <p>Create a new user.</p>
          <h4>Body</h4>
          <pre><code>{`
{
  "name": "Charlie",
  "email": "charlie@example.com"
}
`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>GET /v1/calls</h3>
          <p>List call records.</p>
          <h4>Query Parameters</h4>
          <table>
            <thead><tr><th>Name</th><th>In</th><th>Type</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td>from</td><td>query</td><td>string</td><td>Filter start time (RFC3339)</td></tr>
              <tr><td>to</td><td>query</td><td>string</td><td>Filter end time (RFC3339)</td></tr>
            </tbody>
          </table>
        </article>
        <article className="endpoint">
          <h3>GET /v1/calls/:id</h3>
          <p>Retrieve detailed information for a specific call.</p>
          <h4>Parameters</h4>
          <table>
            <thead><tr><th>Name</th><th>In</th><th>Type</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td>id</td><td>path</td><td>string</td><td>Call identifier</td></tr>
            </tbody>
          </table>
        </article>
        <article className="endpoint">
          <h3>POST /v1/calls</h3>
          <p>Initiate an outbound call.</p>
          <h4>Body</h4>
          <pre><code>{`{
  "from": "+15551234567",
  "to": "+15557654321",
  "answer_url": "https://yourapp.com/webhooks/answer"
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>GET /v1/recordings</h3>
          <p>List call recordings.</p>
          <h4>Query Parameters</h4>
          <table>
            <thead><tr><th>Name</th><th>In</th><th>Type</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td>call_id</td><td>query</td><td>string</td><td>Filter by call identifier</td></tr>
            </tbody>
          </table>
        </article>
        <article className="endpoint">
          <h3>GET /v1/recordings/:id/download</h3>
          <p>Download a recording file.</p>
          <h4>Parameters</h4>
          <table>
            <thead><tr><th>Name</th><th>In</th><th>Type</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td>id</td><td>path</td><td>string</td><td>Recording identifier</td></tr>
            </tbody>
          </table>
        </article>
        <article className="endpoint">
          <h3>GET /v1/numbers</h3>
          <p>List your reserved phone numbers (DIDs).</p>
        </article>
        <article className="endpoint">
          <h3>POST /v1/numbers/reserve</h3>
          <p>Reserve a new phone number.</p>
          <h4>Body</h4>
          <pre><code>{`{
  "country": "US",
  "type": "local"
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>DELETE /v1/numbers/:id</h3>
          <p>Release a reserved phone number.</p>
          <h4>Parameters</h4>
          <table>
            <thead><tr><th>Name</th><th>In</th><th>Type</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td>id</td><td>path</td><td>string</td><td>Number identifier</td></tr>
            </tbody>
          </table>
        </article>
        <article className="endpoint">
          <h3>POST /v1/messages/sms</h3>
          <p>Send an SMS message.</p>
          <h4>Body</h4>
          <pre><code>{`{
  "from": "+15551234567",
  "to": "+15557654321",
  "text": "Hello from VoiceProConnect!"
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>GET /v1/messages/sms/:id</h3>
          <p>Retrieve the status of an SMS message.</p>
        </article>
        <article className="endpoint">
          <h3>POST /v1/ai/conversations</h3>
          <p>Start an AI Agent conversation.</p>
          <h4>Request</h4>
          <pre><code>{`{
  "user_id": "user_123",
  "script_id": "welcome_bot"
}`}</code></pre>
          <h4>Response</h4>
          <pre><code>{`{
  "id": "conv_001",
  "user_id": "user_123",
  "script_id": "welcome_bot",
  "status": "in_progress",
  "started_at": "2025-04-22T12:00:00Z"
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>GET /v1/ai/conversations/:id</h3>
          <p>Retrieve the conversation transcript.</p>
          <h4>Response</h4>
          <pre><code>{`{
  "id": "conv_001",
  "user_id": "user_123",
  "script_id": "welcome_bot",
  "status": "completed",
  "messages": [
    { "from": "bot", "text": "Welcome! How can I assist you today?", "timestamp": "2025-04-22T12:00:05Z" },
    { "from": "user", "text": "I need help with my account.", "timestamp": "2025-04-22T12:00:15Z" }
  ]
}`}</code></pre>
        </article>
        {/* --- Authentication --- */}
        <article className="endpoint">
          <h3>POST /v1/auth/login</h3>
          <p>Authenticate and retrieve an access token.</p>
          <h4>Body</h4>
          <pre><code>{`{
  "email": "user@example.com",
  "password": "YourPassword"
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>POST /v1/auth/logout</h3>
          <p>Invalidate the current access token.</p>
        </article>
        <article className="endpoint">
          <h3>GET /v1/auth/me</h3>
          <p>Retrieve details for the currently authenticated user.</p>
        </article>
        {/* --- User Management --- */}
        <article className="endpoint">
          <h3>PUT /v1/users/:id</h3>
          <p>Update a user's profile.</p>
          <h4>Body</h4>
          <pre><code>{`{
  "name": "New Name",
  "email": "new@example.com"
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>DELETE /v1/users/:id</h3>
          <p>Delete a user account.</p>
        </article>
        {/* --- Fax Messaging --- */}
        <article className="endpoint">
          <h3>POST /v1/messages/fax</h3>
          <p>Send a fax.</p>
          <h4>Body</h4>
          <pre><code>{`{
  "from": "+15551234567",
  "to": "+15557654321",
  "file_url": "https://yourapp.com/doc.pdf"
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>GET /v1/messages/fax</h3>
          <p>List fax transmissions.</p>
        </article>
        <article className="endpoint">
          <h3>GET /v1/messages/fax/:id/download</h3>
          <p>Download a fax file.</p>
        </article>
        {/* --- Conferencing --- */}
        <article className="endpoint">
          <h3>POST /v1/conferences</h3>
          <p>Create a new conference room.</p>
          <h4>Body</h4>
          <pre><code>{`{
  "name": "Team Meeting",
  "hosts": ["user_123"],
  "participants": ["user_456"]
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>GET /v1/conferences/:id</h3>
          <p>Get details for a conference room.</p>
        </article>
        <article className="endpoint">
          <h3>DELETE /v1/conferences/:id</h3>
          <p>End a conference room.</p>
        </article>
        {/* --- Voicemail --- */}
        <article className="endpoint">
          <h3>GET /v1/voicemails</h3>
          <p>List voicemail messages.</p>
        </article>
        <article className="endpoint">
          <h3>GET /v1/voicemails/:id/download</h3>
          <p>Download a voicemail recording.</p>
        </article>
        {/* --- IVR Flows --- */}
        <article className="endpoint">
          <h3>GET /v1/ivr-flows</h3>
          <p>List IVR flow definitions.</p>
          <h4>Response</h4>
          <pre><code>{`{
  "data": [
    { "id": "ivr_001", "name": "Main Menu", "description": "Company IVR", "steps": [/* ... */] }
  ]
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>POST /v1/ivr-flows</h3>
          <p>Create a new IVR flow.</p>
          <h4>Request</h4>
          <pre><code>{`{
  "name": "Main Menu",
  "description": "Company IVR",
  "steps": [
    { "type": "prompt", "text": "Press 1 for Sales, 2 for Support" },
    { "type": "input", "digit": 1, "action": { "type": "transfer", "target": "queue_sales" } }
  ]
}`}</code></pre>
          <h4>Response</h4>
          <pre><code>{`{
  "id": "ivr_002",
  "name": "Main Menu",
  "description": "Company IVR",
  "active": true
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>GET /v1/ivr-flows/:id</h3>
          <p>Get an IVR flow definition.</p>
          <h4>Response</h4>
          <pre><code>{`{
  "id": "ivr_002",
  "name": "Main Menu",
  "description": "Company IVR",
  "steps": [
    { "type": "prompt", "text": "Press 1 for Sales, 2 for Support" },
    { "type": "input", "digit": 1, "action": { "type": "transfer", "target": "queue_sales" } }
  ]
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>PUT /v1/ivr-flows/:id</h3>
          <p>Update an existing IVR flow.</p>
          <h4>Request</h4>
          <pre><code>{`{
  "description": "Updated IVR Menu",
  "active": false
}`}</code></pre>
          <h4>Response</h4>
          <pre><code>{`{
  "id": "ivr_002",
  "name": "Main Menu",
  "description": "Updated IVR Menu",
  "active": false
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>DELETE /v1/ivr-flows/:id</h3>
          <p>Delete an IVR flow.</p>
          <h4>Response</h4>
          <pre><code>{`HTTP 204 No Content`}</code></pre>
        </article>
        {/* --- Webhooks --- */}
        <article className="endpoint">
          <h3>GET /v1/webhooks</h3>
          <p>List registered webhooks.</p>
          <h4>Response</h4>
          <pre><code>{`{
  "data": [
    { "id": "wh_001", "event": "call.completed", "target_url": "https://example.com/webhook", "active": true }
  ]
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>POST /v1/webhooks</h3>
          <p>Register a new webhook endpoint.</p>
          <h4>Request</h4>
          <pre><code>{`{
  "event": "call.completed",
  "target_url": "https://example.com/webhook"
}`}</code></pre>
          <h4>Response</h4>
          <pre><code>{`{
  "id": "wh_002",
  "event": "call.completed",
  "target_url": "https://example.com/webhook",
  "active": true
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>DELETE /v1/webhooks/:id</h3>
          <p>Delete a webhook registration.</p>
          <h4>Response</h4>
          <pre><code>{`HTTP 204 No Content`}</code></pre>
        </article>
        {/* --- Billing & Reports --- */}
        <article className="endpoint">
          <h3>GET /v1/billing/invoices</h3>
          <p>Retrieve a list of invoices.</p>
          <h4>Response</h4>
          <pre><code>{`{
  "data": [
    { "id": "inv_001", "date": "2025-03-31", "amount": 123.45, "status": "paid" }
  ]
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>GET /v1/billing/invoices/:id</h3>
          <p>Retrieve invoice details.</p>
          <h4>Response</h4>
          <pre><code>{`{
  "id": "inv_001",
  "date": "2025-03-31",
  "amount": 123.45,
  "status": "paid",
  "line_items": [
    { "description": "Base Subscription", "quantity": 10, "unit_price": 29, "total": 290 },
    { "description": "SMS overage", "quantity": 50, "unit_price": 0.05, "total": 2.5 }
  ]
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>GET /v1/billing/usage</h3>
          <p>Retrieve usage summary (query: <code>?month=YYYY-MM</code>).</p>
          <h4>Response</h4>
          <pre><code>{`{
  "month": "2025-03",
  "calls_minutes": 1024,
  "sms_count": 200,
  "fax_pages": 30,
  "ai_minutes": 120,
  "total_charges": 350.75
}`}</code></pre>
        </article>
        {/* --- Support Tickets --- */}
        <article className="endpoint">
          <h3>GET /v1/tickets</h3>
          <p>List support tickets.</p>
          <h4>Response</h4>
          <pre><code>{`{
  "data": [
    { "id": "ticket_001", "subject": "Unable to call", "status": "open", "created_at": "2025-04-20T12:00:00Z" }
  ]
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>POST /v1/tickets</h3>
          <p>Create a support ticket.</p>
          <h4>Request</h4>
          <pre><code>{`{
  "subject": "Issue subject",
  "description": "Detailed description...",
  "priority": "high"
}`}</code></pre>
          <h4>Response</h4>
          <pre><code>{`{
  "id": "ticket_002",
  "subject": "Issue subject",
  "status": "open"
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>GET /v1/tickets/:id</h3>
          <p>Get ticket details.</p>
          <h4>Response</h4>
          <pre><code>{`{
  "id": "ticket_002",
  "subject": "Issue subject",
  "description": "Detailed description...",
  "status": "open",
  "messages": [
    { "from": "user", "text": "help!", "timestamp": "2025-04-20T12:01:00Z" }
  ]
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>PUT /v1/tickets/:id</h3>
          <p>Update ticket status or add a comment.</p>
          <h4>Request</h4>
          <pre><code>{`{
  "status": "closed",
  "comment": "We have resolved your issue."
}`}</code></pre>
          <h4>Response</h4>
          <pre><code>{`{
  "id": "ticket_002",
  "status": "closed"
}`}</code></pre>
        </article>
        {/* --- Integrations --- */}
        <article className="endpoint">
          <h3>GET /v1/integrations</h3>
          <p>List custom integrations.</p>
          <h4>Response</h4>
          <pre><code>{`{
  "data": [
    { "id": "int_001", "name": "Salesforce Sync", "type": "crm", "active": true }
  ]
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>POST /v1/integrations</h3>
          <p>Create a new integration configuration.</p>
          <h4>Request</h4>
          <pre><code>{`{
  "name": "Zendesk Connector",
  "type": "helpdesk",
  "config": { "api_key": "abcd1234" }
}`}</code></pre>
          <h4>Response</h4>
          <pre><code>{`{
  "id": "int_002",
  "name": "Zendesk Connector",
  "type": "helpdesk",
  "active": true
}`}</code></pre>
        </article>
        <article className="endpoint">
          <h3>DELETE /v1/integrations/:id</h3>
          <p>Delete an integration configuration.</p>
          <h4>Response</h4>
          <pre><code>{`HTTP 204 No Content`}</code></pre>
        </article>
      </section>
      <section className="api-auth">
        <h2>Authentication Example</h2>
        <pre><code>{`curl -X GET \
  -H "Authorization: Bearer YOUR_TOKEN" \
  https://api.voiceproconnect.com/v1/users`}</code></pre>
      </section>
    </div>
  );
}